<script setup>
import { ref, computed } from 'vue'
import { formatTime } from '@/utils/datetime'
import { ChevronDown, ChevronRight, MapPin } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Point, LineString, Polygon, MultiPolygon } from '@/components/icons'

const props = defineProps({
  feature: {
    type: Object,
    required: true
  },
  settings: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['highlight'])

const showDetails = ref(false)

const toggleDetails = () => {
  showDetails.value = !showDetails.value
}

const highlightFeature = (event) => {
  event.stopPropagation()
  if (props.feature) {
    emit('highlight', props.feature)
  }
}

const geometryType = computed(() => {
  return props.feature?.geometry?.type?.toLowerCase() || 'polygon'
})
</script>

<template>
  <div
    class="py-1.5 mb-0 px-2 hover:bg-muted/50 cursor-pointer rounded-md"
    @click="toggleDetails"
  >
    <div class="flex items-start justify-between">
      <div class="flex-1">
        <div class="flex gap-2">
          <div
            class="w-5 h-5 rounded-full flex-shrink-0 mt-0.5 flex items-center justify-center overflow-hidden"
            :style="`background-color: ${props.feature.properties.color || props.feature.properties.template_color || '#0059ff'}`">
            <Point v-if="geometryType === 'point'" class="w-3 h-3 fill-white" />
            <LineString v-if="geometryType === 'linestring'" class="w-3 h-3 fill-white" />
            <Polygon v-if="geometryType === 'polygon'" class="w-3 h-3 fill-white" />
            <MultiPolygon v-if="geometryType === 'multipolygon'" class="w-3 h-3 fill-white" />
            <Polygon v-if="geometryType === 'geometrycollection'" class="w-3 h-3 fill-white" />
          </div>
          <div class="flex-1">
            <!-- Name -->
            <div class="text-sm">{{ props.feature.properties.name?.trim() || 'Unnamed' }}</div>

            <!-- Template data below name -->
            <div v-if="props.feature.properties.data" class="text-xs mt-0.5">
              <span v-if="typeof props.feature.properties.data === 'object'" class="flex flex-wrap gap-x-3 gap-y-0.5">
                <span
                  v-for="(value, key) in props.feature.properties.data"
                  :key="key"
                  class="text-muted-foreground"
                >
                  {{ value.key }}: {{ value.value }}
                </span>
              </span>
              <span v-else class="text-muted-foreground">
                {{ props.feature.properties.data }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Chevron indicator -->
      <component
        :is="showDetails ? ChevronDown : ChevronRight"
        class="h-3.5 w-3.5 text-gray-400 ml-2 mt-0.5"
      />
    </div>

    <!-- Collapsible details section -->
    <div v-if="showDetails" class="mt-1 pt-1 ml-5 border-t border-gray-100 space-y-0.5 text-xs">
      <!-- Geometry type and date -->
      <div class="flex items-center gap-3 text-muted-foreground">
        <span>{{ props.feature.geometry.type }}</span>
        <span>{{ formatTime(props.feature.properties.created_at, props.settings.tz, 'D MMM YYYY HH:mm') }}</span>
      </div>

      <!-- Project info -->
      <div v-if="props.feature.properties.project_names && props.feature.properties.project_names.length > 0">
        <div class="flex items-start gap-1">
          <span class="text-muted-foreground">Project:</span>
          <span class="font-medium">{{ props.feature.properties.project_names.join(', ') }}</span>
        </div>
      </div>

      <!-- Organization info -->
      <div v-if="props.feature.properties.organization_names && props.feature.properties.organization_names.length > 0">
        <div class="flex items-start gap-1">
          <span class="text-muted-foreground">Organization:</span>
          <span class="font-medium">{{ props.feature.properties.organization_names.join(', ') }}</span>
        </div>
      </div>

      <!-- Show on Map button -->
      <div class="pt-1">
        <Button
          @click="highlightFeature"
          variant="outline"
          size="sm"
          class="h-7 px-2 text-xs"
        >
          <MapPin class="h-3 w-3 mr-1" />
          Show on Map
        </Button>
      </div>
    </div>
  </div>
</template>
